import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ClearanceCase, ClearanceDocument, Declaration, LineItem,
  getCase,
  getDocuments, uploadDocument, deleteDocument, parseDocuments,
  getDeclaration, generateDeclaration, updateDeclaration, approveDeclaration,
  submitDeclaration, generateXml,
  downloadXmlUrl, printHtmlUrl, downloadPdfUrl, documentFileUrl,
} from '../services/clearanceService';
import { usePermission } from '../hooks/usePermission';
import { useMessage } from '../contexts/MessageContext';
import { validateSession } from '../utils/sessionValidator';
import FunctionPageHeader from '../components/FunctionPageHeader';
import '../styles/DataTable.css';
import { formatLocal } from '../utils/dateHelper';

const DOC_TYPES = [
  { code: 'commercial_invoice', label: '商業發票 Invoice' },
  { code: 'packing_list', label: '裝箱單 Packing List' },
  { code: 'air_waybill', label: '空運提單 AWB' },
];

const PARSE_STATUS_COLORS: Record<string, string> = {
  pending: '#6c757d',
  running: '#fd7e14',
  processing: '#fd7e14',
  done: '#198754',
  failed: '#dc3545',
};

const STATUS_COLORS: Record<string, string> = {
  draft: '#6c757d', docs_uploaded: '#0dcaf0', parsing: '#fd7e14',
  parse_failed: '#dc3545', parsed: '#17a2b8',
  reviewing: '#6610f2', approved: '#198754', xml_ready: '#0d6efd',
  filed: '#20c997', rejected: '#dc3545',
};

const UPLOADABLE = new Set(['draft', 'docs_uploaded', 'parse_failed']);
const TRAINING_ELIGIBLE = new Set(['parsed', 'reviewing', 'approved', 'xml_ready', 'filed']);

const ClearanceCaseDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const caseId = Number(id);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasPermission, loading: permLoading } = usePermission();
  const { showSuccess, showError, showApiError, showInfo } = useMessage();
  const hasInit = useRef(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [activeTab, setActiveTab] = useState(0);
  const [caseData, setCaseData] = useState<ClearanceCase | null>(null);
  const [documents, setDocuments] = useState<ClearanceDocument[]>([]);
  const [declaration, setDeclaration] = useState<Declaration | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [genXml, setGenXml] = useState(false);

  const [uploadType, setUploadType] = useState('commercial_invoice');
  const [selectedIsZip, setSelectedIsZip] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const [editDecl, setEditDecl] = useState<Partial<Declaration>>({});
  const [declChanged, setDeclChanged] = useState(false);

  const load = useCallback(async () => {
    try {
      const [cData, docs] = await Promise.all([getCase(caseId), getDocuments(caseId)]);
      setCaseData(cData);
      setDocuments(docs);
      return cData;
    } catch (err: any) { showApiError(err); return null; }
  }, [caseId, showApiError]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDeclaration = useCallback(async () => {
    try {
      const d = await getDeclaration(caseId);
      setDeclaration(d);
      setEditDecl(d);
      setDeclChanged(false);
    } catch {
      setDeclaration(null);
    }
  }, [caseId]);

  useEffect(() => {
    if (!permLoading && !hasInit.current) {
      hasInit.current = true;
      const init = async () => {
        const valid = await validateSession('ClearanceCaseDetailPage');
        if (!valid) return;
        setLoading(true);
        const cData = await load();
        // 解析中尚未有報單，跳過 getDeclaration 避免 404 console 錯誤
        if (cData && cData.status !== 'parsing') {
          await loadDeclaration();
        }
        setLoading(false);
      };
      init();
    }
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [permLoading, load, loadDeclaration]); // eslint-disable-line react-hooks/exhaustive-deps

  // 解析中自動 polling（每 10 秒），解析完後載入報單
  useEffect(() => {
    const isParsing = caseData?.status === 'parsing' ||
      documents.some(d => d.parse_status === 'running');

    if (!isParsing) {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
        loadDeclaration(); // 剛完成解析，嘗試取最新報單
      }
      return;
    }

    if (!pollingRef.current) {
      pollingRef.current = setInterval(() => { load(); }, 10000);
    }
  }, [caseData?.status, documents, load, loadDeclaration]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Tab 1: 文件管理 ─────────────────────────────────────────────────────────

  const isZipFile = (f: File) => f.name.toLowerCase().endsWith('.zip');

  const handleUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { showError('ERR020006', { field: t('clearance.doc.file', '請選擇檔案') }); return; }
    setUploading(true);
    try {
      const docs = await uploadDocument(caseId, file, isZipFile(file) ? undefined : uploadType);
      const count = docs.length;
      showSuccess('SYS020001', { name: count > 1 ? `${count} 份文件（ZIP解壓）` : t('clearance.doc.title', '文件') });
      if (fileRef.current) fileRef.current.value = '';
      setSelectedIsZip(false);
      await load();
    } catch (err: any) { showApiError(err); }
    finally { setUploading(false); }
  };

  const handleDeleteDoc = async (docId: number) => {
    if (!window.confirm(t('clearance.doc.confirmDelete', '確定要刪除此文件嗎？'))) return;
    try {
      await deleteDocument(caseId, docId);
      showSuccess('SYS020003', { name: t('clearance.doc.title', '文件') });
      await load();
    } catch (err: any) { showApiError(err); }
  };

  const handleParse = async () => {
    setParsing(true);
    try {
      await parseDocuments(caseId);
      // lint-msg-allow: uses i18n key for async-started notification, not a sys_message_code
      showInfo('clearance.doc.parseStarted');
      await load();
    } catch (err: any) { showApiError(err); }
    finally { setParsing(false); }
  };

  // ─── Tab 2: 報單審核 ─────────────────────────────────────────────────────────

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const d = await generateDeclaration(caseId);
      setDeclaration(d);
      setEditDecl(d);
      setDeclChanged(false);
      showSuccess('SYS020001', { name: t('clearance.decl.title', '報單') });
      await load();
    } catch (err: any) { showApiError(err); }
    finally { setGenerating(false); }
  };

  const handleSaveDecl = async () => {
    if (!declChanged) return;
    setSaving(true);
    try {
      const d = await updateDeclaration(caseId, editDecl);
      setDeclaration(d);
      setEditDecl(d);
      setDeclChanged(false);
      showSuccess('SYS020002', { name: t('clearance.decl.title', '報單') });
    } catch (err: any) { showApiError(err); }
    finally { setSaving(false); }
  };

  const handleApprove = async () => {
    if (!window.confirm(t('clearance.decl.confirmApprove', '確定核准此報單？核准後需重新產生才能修改。'))) return;
    setApproving(true);
    try {
      await approveDeclaration(caseId);
      showSuccess('SYS020002', { name: t('clearance.decl.title', '報單') });
      await loadDeclaration();
      await load();
    } catch (err: any) { showApiError(err); }
    finally { setApproving(false); }
  };

  const setDeclField = (k: keyof Declaration, v: any) => {
    setEditDecl(d => ({ ...d, [k]: v }));
    setDeclChanged(true);
  };

  // ─── Tab 3: XML 輸出 ─────────────────────────────────────────────────────────

  const handleGenXml = async () => {
    setGenXml(true);
    try {
      const result = await generateXml(caseId);
      showSuccess('SYS020001', { name: `NX5105 XML (${result.schema_version})` });
      await loadDeclaration();
      await load();
    } catch (err: any) { showApiError(err); }
    finally { setGenXml(false); }
  };

  const handleSubmit = async () => {
    if (!window.confirm(t('clearance.xml.confirmSubmit', '確定要申報備存？申報後案件狀態將改為「已申報」。'))) return;
    setSubmitting(true);
    try {
      await submitDeclaration(caseId);
      showSuccess('SYS020002', { name: t('clearance.xml.filing', '申報備存') });
      await loadDeclaration();
      await load();
    } catch (err: any) { showApiError(err); }
    finally { setSubmitting(false); }
  };

  const openNewTab = (url: string) => window.open(url, '_blank');

  // ─── Render ──────────────────────────────────────────────────────────────────

  if (permLoading || loading) return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  if (!hasPermission('ee_clearance_cases', 'read')) return <div className="page-container"><div className="error-message">{t('common.noPermission')}</div></div>;

  const canUpdate = hasPermission('ee_clearance_cases', 'update');
  const status = caseData?.status || '';

  const tabs = [
    t('clearance.tab.docs', '📄 文件管理'),
    t('clearance.tab.decl', '📋 報單審核'),
    t('clearance.tab.xml', '🖥 XML輸出'),
  ];

  return (
    <div className="page-container">
      <div className="page-header" style={{ alignItems: 'flex-start' }}>
        <div>
          <FunctionPageHeader funcCode="ee_clearance_cases" />
          <div style={{ fontSize: 13, color: '#6c757d', marginTop: 4 }}>
            <button className="btn-secondary" style={{ padding: '2px 10px', fontSize: 12, marginRight: 10 }}
              onClick={() => navigate('/ee_clearance_cases')}>
              ← {t('common.back')}
            </button>
            {caseData && (
              <>
                <strong>{caseData.case_no}</strong>
                {caseData.hawb_no && <span style={{ marginLeft: 8 }}>HAWB: {caseData.hawb_no}</span>}
                <span style={{ marginLeft: 12 }}>
                  <span style={badgeStyle(STATUS_COLORS[status] || '#6c757d')}>{status}</span>
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '2px solid #dee2e6', marginBottom: 20 }}>
        {tabs.map((label, i) => (
          <button key={i} onClick={() => setActiveTab(i)}
            style={{
              padding: '10px 20px', border: 'none', borderBottom: activeTab === i ? '3px solid #0d6efd' : '3px solid transparent',
              background: 'none', fontWeight: activeTab === i ? 700 : 400, color: activeTab === i ? '#0d6efd' : '#495057',
              cursor: 'pointer', fontSize: 14, marginBottom: -2,
            }}>
            {label}
          </button>
        ))}
      </div>

      {/* ── Tab 0: 文件管理 ─────────────────────────────────────────────────────── */}
      {activeTab === 0 && (
        <div>
          {canUpdate && UPLOADABLE.has(status) && (
            <div style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 8, padding: 20, marginBottom: 20 }}>
              <h4 style={{ margin: '0 0 12px', fontSize: 14 }}>{t('clearance.doc.upload', '上傳文件')}</h4>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <input type="file" ref={fileRef} accept=".pdf,.jpg,.jpeg,.png,.tiff,.zip"
                  style={{ fontSize: 13 }}
                  onChange={e => {
                    const f = e.target.files?.[0];
                    const zip = !!f && isZipFile(f);
                    setSelectedIsZip(zip);
                    if (!zip && !uploadType) setUploadType('commercial_invoice');
                  }} />
                {/* ZIP 自動辨識，無需選擇類型 */}
                {!selectedIsZip && (
                  <select value={uploadType} onChange={e => setUploadType(e.target.value)}
                    style={{ padding: '7px 10px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 13 }}>
                    {DOC_TYPES.map(d => <option key={d.code} value={d.code}>{d.label}</option>)}
                  </select>
                )}
                {selectedIsZip && (
                  <span style={{ fontSize: 12, color: '#6c757d', padding: '7px 0' }}>ZIP — 自動辨識每份文件類型</span>
                )}
                <button className="btn-primary" onClick={handleUpload} disabled={uploading}>
                  {uploading ? t('common.loading') : t('clearance.doc.uploadBtn', '上傳')}
                </button>
              </div>
            </div>
          )}

          <table className="data-table">
            <thead>
              <tr>
                <th>{t('clearance.doc.type', '類型')}</th>
                <th>{t('clearance.doc.format', '格式')}</th>
                <th>{t('clearance.doc.fileName', '檔名')}</th>
                <th>{t('clearance.doc.parseStatus', '解析狀態')}</th>
                <th>{t('clearance.doc.confidence', '可信度')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {documents.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 24, color: '#6c757d' }}>{t('common.noData')}</td></tr>
              ) : documents.map(doc => (
                <tr key={doc.id}>
                  <td>{DOC_TYPES.find(d => d.code === doc.doc_type)?.label || doc.doc_type}</td>
                  <td><span style={badgeStyle('#6c757d')}>{doc.doc_format}</span></td>
                  <td style={{ fontSize: 12 }}>{doc.file_name}</td>
                  <td>
                    <span style={badgeStyle(PARSE_STATUS_COLORS[doc.parse_status] || '#6c757d')}>
                      {doc.parse_status}
                    </span>
                    {doc.parse_status === 'running' && doc.parse_started_at && (
                      <span style={{ marginLeft: 6, color: '#fd7e14', fontSize: 11 }}>
                        {formatElapsed(doc.parse_started_at)}
                      </span>
                    )}
                    {doc.parse_error && doc.parse_status !== 'done' && <span style={{ marginLeft: 6, color: '#dc3545', fontSize: 11 }}>{doc.parse_error}</span>}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {doc.parse_confidence != null ? `${Math.round(doc.parse_confidence * 100)}%` : '—'}
                  </td>
                  <td style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <button className="btn-secondary" style={{ padding: '3px 10px', fontSize: 12 }}
                      onClick={() => openNewTab(documentFileUrl(caseId, doc.id))}>
                      {t('common.view', '檢視')}
                    </button>
                    {canUpdate && UPLOADABLE.has(status) && (
                      <button className="btn-danger" style={{ padding: '3px 10px', fontSize: 12 }}
                        onClick={() => handleDeleteDoc(doc.id)}>
                        {t('common.delete')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {canUpdate && documents.length > 0 && UPLOADABLE.has(status) && (
            <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
              <button className="btn-primary" onClick={handleParse} disabled={parsing}>
                {parsing ? t('common.loading') : '🤖 AI 解析所有文件'}
              </button>
              <span style={{ fontSize: 12, color: '#6c757d', alignSelf: 'center' }}>
                解析完成後，案件狀態會自動更新
              </span>
            </div>
          )}

          {/* 訓練中心入口（案件解析後才顯示） */}
          {TRAINING_ELIGIBLE.has(status) && (
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn-secondary" style={{ padding: '4px 14px', fontSize: 12 }}
                onClick={() => navigate('/ee_clearance_training')}>
                🧠 訓練中心
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Tab 1: 報單審核 ─────────────────────────────────────────────────────── */}
      {activeTab === 1 && (
        <div>
          {!declaration ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <p style={{ color: '#6c757d', marginBottom: 16 }}>
                {t('clearance.decl.notGenerated', '尚未產生報單。請先完成文件解析，再產生報單。')}
              </p>
              {canUpdate && ['docs_uploaded', 'parsed', 'parse_failed', 'reviewing', 'approved', 'rejected'].includes(status) && (
                <button className="btn-primary" onClick={handleGenerate} disabled={generating}>
                  {generating ? t('common.loading') : t('clearance.decl.generate', '✨ 產生報單')}
                </button>
              )}
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                {canUpdate && ['reviewing', 'draft'].includes(declaration.review_status) && (
                  <>
                    <button className="btn-secondary" onClick={handleGenerate} disabled={generating}>
                      {generating ? t('common.loading') : t('clearance.decl.regenerate', '重新產生')}
                    </button>
                    <button className="btn-primary" onClick={handleSaveDecl} disabled={saving || !declChanged}>
                      {saving ? t('common.saving', '儲存中…') : t('common.save')}
                    </button>
                  </>
                )}
                {canUpdate && declaration.review_status === 'reviewing' && (
                  <button className="btn-primary" style={{ background: '#198754' }} onClick={handleApprove} disabled={approving}>
                    {approving ? t('common.loading') : t('clearance.decl.approve', '✅ 核准報單')}
                  </button>
                )}
                <span style={badgeStyle(declaration.review_status === 'approved' ? '#198754' : '#6c757d')}>
                  {declaration.review_status}
                </span>
                {declaration.xml_schema_version && (
                  <span style={{ fontSize: 12, color: '#6c757d', alignSelf: 'center' }}>
                    Schema: {declaration.xml_schema_version}
                  </span>
                )}
              </div>

              {/* 報單表頭 */}
              <section style={sectionStyle}>
                <h4 style={sectionTitle}>{t('clearance.decl.header', '報單表頭')}</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                  <DeclField label={t('clearance.decl.declType', '申報類別')} value={editDecl.declaration_type || ''} disabled={!canUpdate || declaration.review_status === 'approved'} onChange={v => setDeclField('declaration_type', v)} />
                  <DeclField label={t('clearance.decl.currency', '幣別')} value={editDecl.currency || ''} disabled={!canUpdate || declaration.review_status === 'approved'} onChange={v => setDeclField('currency', v)} />
                  <DeclField label={t('clearance.decl.exchangeRate', '匯率')} value={editDecl.exchange_rate || ''} disabled={!canUpdate || declaration.review_status === 'approved'} onChange={v => setDeclField('exchange_rate', v)} />
                  <DeclField label={t('clearance.decl.invoiceAmount', '發票金額')} value={editDecl.invoice_amount || ''} disabled={!canUpdate || declaration.review_status === 'approved'} onChange={v => setDeclField('invoice_amount', v)} />
                  <DeclField label={t('clearance.decl.freightAmount', '運費')} value={editDecl.freight_amount || ''} disabled={!canUpdate || declaration.review_status === 'approved'} onChange={v => setDeclField('freight_amount', v)} />
                  <DeclField label={t('clearance.decl.insuranceAmount', '保費')} value={editDecl.insurance_amount || ''} disabled={!canUpdate || declaration.review_status === 'approved'} onChange={v => setDeclField('insurance_amount', v)} />
                  <DeclField label={t('clearance.decl.cifAmountTwd', 'CIF 台幣')} value={declaration.cif_amount_twd != null ? String(declaration.cif_amount_twd) : ''} disabled={true} onChange={() => {}} />
                  <DeclField label={t('clearance.decl.totalPackages', '總箱數')} value={editDecl.total_packages != null ? String(editDecl.total_packages) : ''} disabled={!canUpdate || declaration.review_status === 'approved'} onChange={v => setDeclField('total_packages', Number(v))} />
                  <DeclField label={t('clearance.decl.grossWeight', '總毛重 KG')} value={editDecl.gross_weight_kg || ''} disabled={!canUpdate || declaration.review_status === 'approved'} onChange={v => setDeclField('gross_weight_kg', v)} />
                </div>
              </section>

              {/* 進口商資料 */}
              <section style={sectionStyle}>
                <h4 style={sectionTitle}>{t('clearance.decl.importer', '進口商資料')}</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <DeclField label={t('clearance.decl.importerTaxId', '統一編號')} value={editDecl.importer_tax_id || ''} disabled={!canUpdate || declaration.review_status === 'approved'} onChange={v => setDeclField('importer_tax_id', v)} />
                  <DeclField label={t('clearance.decl.importerNameZh', '中文名稱')} value={editDecl.importer_name_zh || ''} disabled={!canUpdate || declaration.review_status === 'approved'} onChange={v => setDeclField('importer_name_zh', v)} />
                  <DeclField label={t('clearance.decl.importerNameEn', '英文名稱')} value={editDecl.importer_name_en || ''} disabled={!canUpdate || declaration.review_status === 'approved'} onChange={v => setDeclField('importer_name_en', v)} />
                  <DeclField label={t('clearance.decl.importerAddressEn', '英文地址')} value={editDecl.importer_address_en || ''} disabled={!canUpdate || declaration.review_status === 'approved'} onChange={v => setDeclField('importer_address_en', v)} />
                  <DeclField label={t('clearance.decl.tradeCondition', '貿易條件')} value={editDecl.trade_condition || ''} disabled={!canUpdate || declaration.review_status === 'approved'} onChange={v => setDeclField('trade_condition', v)} />
                  <DeclField label={t('clearance.decl.dutyMethodCode', '納稅辦法')} value={editDecl.duty_method_code || ''} disabled={!canUpdate || declaration.review_status === 'approved'} onChange={v => setDeclField('duty_method_code', v)} />
                </div>
              </section>

              {/* 品項明細 */}
              <section style={sectionStyle}>
                <h4 style={sectionTitle}>{t('clearance.decl.lineItems', '品項明細')}</h4>
                <table className="data-table" style={{ fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th>序</th>
                      <th>HS Code</th>
                      <th>{t('clearance.decl.descriptionEn', '品名')}</th>
                      <th>{t('clearance.decl.qty', '數量')}</th>
                      <th>{t('clearance.decl.unit', '單位')}</th>
                      <th>{t('clearance.decl.unitPriceUsd', '單價 USD')}</th>
                      <th>{t('clearance.decl.amountUsd', '金額 USD')}</th>
                      <th>{t('clearance.decl.grossWeightKg', '毛重 KG')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(editDecl.line_items || []).length === 0 ? (
                      <tr><td colSpan={8} style={{ textAlign: 'center', color: '#6c757d', padding: 12 }}>{t('common.noData')}</td></tr>
                    ) : (editDecl.line_items || []).map((item: LineItem, idx: number) => (
                      <tr key={idx}>
                        <td>{item.seq}</td>
                        <td>{item.hs_code}</td>
                        <td>{item.description_en}</td>
                        <td>{item.qty}</td>
                        <td>{item.unit}</td>
                        <td>{item.unit_price_usd}</td>
                        <td>{item.amount_usd}</td>
                        <td>{item.gross_weight_kg}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            </>
          )}
        </div>
      )}

      {/* ── Tab 2: XML 輸出 ─────────────────────────────────────────────────────── */}
      {activeTab === 2 && (
        <div>
          {!declaration ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#6c757d' }}>
              {t('clearance.xml.noDecl', '請先在「報單審核」頁籤核准報單，再產生 XML。')}
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 20, maxWidth: 700 }}>
              {/* 狀態卡片 */}
              <section style={sectionStyle}>
                <h4 style={sectionTitle}>{t('clearance.xml.status', '申報狀態')}</h4>
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 14 }}>
                  <div><strong>{t('clearance.decl.reviewStatus', '審核狀態')}：</strong>
                    <span style={badgeStyle(declaration.review_status === 'approved' ? '#198754' : '#6c757d')}>
                      {declaration.review_status}
                    </span>
                  </div>
                  <div><strong>{t('clearance.xml.filingStatus', '申報狀態')}：</strong>
                    <span style={badgeStyle(declaration.filing_status === 'submitted' ? '#20c997' : declaration.filing_status === 'accepted' ? '#198754' : '#6c757d')}>
                      {declaration.filing_status}
                    </span>
                  </div>
                  {declaration.xml_schema_version && (
                    <div><strong>XML Schema：</strong>{declaration.xml_schema_version}</div>
                  )}
                  {declaration.xml_generated_at && (
                    <div><strong>{t('clearance.xml.generatedAt', '產生時間')}：</strong>
                      {formatLocal(declaration.xml_generated_at)}
                    </div>
                  )}
                  {declaration.filing_ref_no && (
                    <div><strong>{t('clearance.xml.filingRefNo', '申報號碼')}：</strong>
                      <strong style={{ color: '#0d6efd' }}>{declaration.filing_ref_no}</strong>
                    </div>
                  )}
                </div>
              </section>

              {/* 操作按鈕 */}
              <section style={sectionStyle}>
                <h4 style={sectionTitle}>{t('clearance.xml.actions', '操作')}</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {/* 產生 XML */}
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <button className="btn-primary"
                      disabled={declaration.review_status !== 'approved' || genXml}
                      onClick={handleGenXml}>
                      {genXml ? t('common.loading') : t('clearance.xml.generate', '⚙️ 產生 NX5105 XML')}
                    </button>
                    {declaration.review_status !== 'approved' && (
                      <span style={{ fontSize: 12, color: '#dc3545' }}>
                        {t('clearance.xml.mustApprove', '需先核准報單')}
                      </span>
                    )}
                  </div>

                  {/* 下載 XML */}
                  {declaration.filing_status !== 'draft' && (
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button className="btn-secondary"
                        onClick={() => openNewTab(downloadXmlUrl(caseId))}>
                        {t('clearance.xml.downloadXml', '⬇ 下載 XML')}
                      </button>
                      <button className="btn-secondary"
                        onClick={() => openNewTab(printHtmlUrl(caseId))}>
                        {t('clearance.xml.printHtml', '🖨 列印報單 (HTML)')}
                      </button>
                      <button className="btn-secondary"
                        onClick={() => openNewTab(downloadPdfUrl(caseId))}>
                        {t('clearance.xml.downloadPdf', '📄 下載 PDF')}
                      </button>
                    </div>
                  )}

                  {/* 申報備存 */}
                  {canUpdate && declaration.filing_status === 'xml_ready' && (
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', paddingTop: 8, borderTop: '1px solid #dee2e6' }}>
                      <button className="btn-primary" style={{ background: '#20c997' }}
                        disabled={submitting} onClick={handleSubmit}>
                        {submitting ? t('common.loading') : t('clearance.xml.submit', '📤 申報備存')}
                      </button>
                      <span style={{ fontSize: 12, color: '#6c757d' }}>
                        {t('clearance.xml.submitHint', '標記為申報完成，記錄申報時間')}
                      </span>
                    </div>
                  )}
                </div>
              </section>

              {/* 版本歷史提示 */}
              <div style={{ fontSize: 12, color: '#6c757d' }}>
                {t('clearance.xml.versionHint', `報單版本 v${declaration.version} — 每次重新產生報單會遞增版本號，備存記錄可於後台查閱。`)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const DeclField: React.FC<{ label: string; value: string; disabled: boolean; onChange: (v: string) => void }> =
  ({ label, value, disabled, onChange }) => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 13, fontWeight: 500 }}>
      {label}
      <input value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
        style={{ padding: '6px 8px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 13, background: disabled ? '#f8f9fa' : '#fff' }} />
    </label>
  );

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatElapsed = (startedAt: string): string => {
  const sec = Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000);
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60), s = sec % 60;
  return `${m}m${s}s`;
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const badgeStyle = (bg: string): React.CSSProperties => ({
  display: 'inline-block', padding: '2px 8px', borderRadius: 10,
  background: bg, color: '#fff', fontSize: 11, fontWeight: 600,
});

const sectionStyle: React.CSSProperties = {
  background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 8, padding: '16px 20px',
};

const sectionTitle: React.CSSProperties = {
  margin: '0 0 12px', fontSize: 14, color: '#1565c0',
  borderBottom: '1px solid #dee2e6', paddingBottom: 8,
};

export default ClearanceCaseDetailPage;